import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";
import { CheckCircle } from "lucide-react";
import axios from "axios";
import config from "../../../../config";
import { PENDING_INVITE_KEY } from "../pendingInvite";
import "./auth.scss";

const URL = config.URL;

// Real backend: the invite email links here with an 8-char `code`, and
// accepting requires an authenticated request (POST /onboarding/accept-invite/:code).
// If the visitor isn't logged in yet, we park the code and send them to sign
// in/register — Login.jsx and Registration.jsx consume it after auth succeeds.
function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const navigate = useNavigate();

  const { authToken } = useSelector((state) => ({ authToken: state.auth.authToken }), shallowEqual);

  const [status, setStatus] = useState(authToken ? "accepting" : "needs_auth");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) {
      setStatus("error");
      setError("This invite link is missing its code.");
      return;
    }

    if (!authToken) {
      localStorage.setItem(PENDING_INVITE_KEY, code);
      setStatus("needs_auth");
      return;
    }

    axios.post(`${URL}onboarding/accept-invite/${code}`)
      .then(() => {
        localStorage.removeItem(PENDING_INVITE_KEY);
        setStatus("done");
        setTimeout(() => navigate("/", { replace: true }), 1200);
      })
      .catch((err) => {
        setStatus("error");
        setError(err.response?.data?.message || "Could not accept this invite.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, authToken]);

  if (status === "needs_auth") {
    return (
      <div className="clean-login-card">
        <div className="login-header">
          <div className="verification-icon"><CheckCircle size={28} /></div>
          <h1 className="login-title">You've been invited</h1>
          <p className="login-subtitle">
            Sign in or create an account to accept this invite — we'll add you automatically once you're signed in.
          </p>
        </div>
        <div className="form-group d-flex flex-wrap flex-center" style={{ gap: 10 }}>
          <button type="button" className="submit-btn" onClick={() => navigate("/auth/login")}>
            Sign In
          </button>
          <button type="button" className="submit-btn" onClick={() => navigate("/auth/registration")}>
            Create Account
          </button>
        </div>
      </div>
    );
  }

  if (status === "accepting") {
    return (
      <div className="clean-login-card">
        <div className="login-header">
          <p className="login-subtitle">Joining the organization…</p>
        </div>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="clean-login-card">
        <div className="login-header">
          <div className="verification-icon"><CheckCircle size={28} /></div>
          <h1 className="login-title">You're in</h1>
          <p className="login-subtitle">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="clean-login-card">
      <div className="login-header">
        <h1 className="login-title">Invite unavailable</h1>
        <p className="login-subtitle">{error}</p>
      </div>
      <button type="button" className="submit-btn" onClick={() => navigate("/auth/login")}>
        Go to sign in
      </button>
    </div>
  );
}

export default AcceptInvite;
